import './index.css'
import cn from 'clsx'

import React from 'react'
import GridWithSlider from './GridWithSlider'
export type GridSizes =
  | 'small'
  | 'medium'
  | 'large'
  | 'mlarge'
  | 'xlarge'
  | 'xxlarge'
type Props = {
  children: React.ReactNode
  singleRow?: boolean
  size: GridSizes
}

/**
 * @deprecated Use `Grid` from `components/ds` instead.
 * @see {@link components/ds/Grid}
 * @migration
 * - Replace `<Grid size="...">` with `<Grid columns="..." gap="...">` from components/ds
 * - Size mapping (legacy → DS columns):
 *   - size="small" → columns="6" (approx. 6 columns)
 *   - size="medium" → columns="4" (approx. 4 columns)
 *   - size="large" → columns="3" (approx. 3 columns)
 *   - size="mlarge" → columns="3" (approx. 3 columns)
 *   - size="xlarge" → columns="2" (approx. 2 columns)
 *   - size="xxlarge" → columns="1" (1 column)
 * - singleRow → use `rows="1"` or CSS `grid-auto-flow: column`
 * - Grid.WithSlider sub-component has no direct DS equivalent; consider custom implementation
 * - DS Grid automatically wraps children; no need for Grid-item wrapper divs
 *
 * A responsive grid layout component with configurable sizes and optional single-row mode
 * @param children - The grid items to display
 * @param singleRow - Whether to display items in a single row (defaults to false)
 * @param size - The grid size: 'small', 'medium', 'large', 'mlarge', 'xlarge', or 'xxlarge'
 * @returns A grid container with responsive sizing and item wrapping
 */
const Grid = (props: Props): React.ReactElement<'div'> => {
  return (
    <div
      className={cn('Grid', {
        'Grid--small': props.size === 'small',
        'Grid--medium': props.size === 'medium',
        'Grid--large': props.size === 'large',
        'Grid--mlarge': props.size === 'mlarge',
        'Grid--xlarge': props.size === 'xlarge',
        'Grid--xxlarge': props.size === 'xxlarge',
        'Grid--single-row': props.singleRow,
      })}
    >
      {React.Children.map(
        props.children,
        (child: React.ReactNode, index: number) =>
          child && (
            <div className="Grid-item" key={index}>
              {child}
            </div>
          ),
      )}
    </div>
  )
}

Grid.WithSlider = GridWithSlider
export default Grid
